import { Typography, Button, Container, Box, Paper } from "@mui/material";
import { Download, Android, Apple, LaptopWindows } from "@mui/icons-material";
import { QRCodeSVG } from "qrcode.react";
import locales from "../locales.json";
import { useLang } from "../context/LangContext";

/** Full-bleed wrapper for edge-to-edge styling */
function FullBleed({ children, sx }) {
  return (
    <Box
      sx={{
        position: "relative",
        left: "50%",
        right: "50%",
        marginLeft: "-50vw",
        marginRight: "-50vw",
        width: "100vw",
        ...sx,
      }}
    >
      {children}
    </Box>
  );
}

function DownloadPage() {
  const { lang } = useLang();
  const { download_page } = locales;
  const isMobile = /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
  const microsoftStoreUrl = "https://apps.microsoft.com/detail/9PGTR3G2JRSQ";
  const googlePlayUrl =
    "https://play.google.com/store/apps/details?id=com.mayorsoftware.medicta";
  const appStoreUrl = "https://apps.apple.com/ch/app/medicta/id6778388883";

  return (
    <Box>
      <FullBleed
        sx={{
          position: "relative",
          overflow: "hidden",
          py: { xs: 6, md: 10 },
          marginTop: { xs: -2, md: -2 },
          minHeight: "calc(80vh - 64px)",
          display: "flex",
          alignItems: "center",
          borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
          background:
            "linear-gradient(135deg, rgba(59,130,246,.16), rgba(168,85,247,.16))",

          // LEFT photo
          "&::before": {
            content: '""',
            position: "absolute",
            inset: 0,
            zIndex: 0,
            backgroundImage: "url('/doctor-left.jpg')",
            backgroundRepeat: "no-repeat",
            backgroundPosition: "-60px center",
            backgroundSize: { xs: "0 0", md: "auto 100%" },
            opacity: { md: 0.28, lg: 0.26 },
            filter: "grayscale(100%) contrast(.95) saturate(.85)",
            mixBlendMode: "normal",
            pointerEvents: "none",
          },

          // RIGHT photo
          "&::after": {
            content: '""',
            position: "absolute",
            inset: 0,
            zIndex: 0,
            backgroundImage: "url('/doctor-right.webp')",
            backgroundRepeat: "no-repeat",
            backgroundPosition: "right center",
            backgroundSize: { xs: "0 0", md: "auto 100%" },
            opacity: { md: 0.28, lg: 0.26 },
            filter: "grayscale(100%) contrast(.95) saturate(.85)",
            mixBlendMode: "normal",
            pointerEvents: "none",
          },
        }}
      >
        {/* Overlay SCRIM */}
        <Box
          aria-hidden
          sx={{
            position: "absolute",
            inset: 0,
            zIndex: 1,
            pointerEvents: "none",
            background:
              "linear-gradient(90deg,rgba(143,143,143,0.01) 0%, #dadef8ff 50%, rgba(150,150,150,0.01) 100%)",
            backgroundSize: "100% 100%",
            mixBlendMode: "normal",
          }}
        />

        {/* Content */}
        <Container
          maxWidth="lg"
          sx={{ position: "relative", zIndex: 2, width: "100%" }}
        >
          {/* Title & description */}
          <Typography
            variant="h3"
            align="center"
            sx={{
              fontWeight: 700,
              mb: 2,
              fontSize: { xs: "1.8rem", sm: "2.2rem", md: "2.5rem" },
            }}
          >
            {download_page.title[lang]}
          </Typography>
          <Typography
            variant="body1"
            color="text.secondary"
            align="center"
            sx={{
              mb: { xs: 5, md: 7 },
              fontSize: { xs: "1rem", md: "1.1rem" },
              lineHeight: 1.6,
              maxWidth: 600,
              mx: "auto",
            }}
          >
            {download_page.description[lang]}
          </Typography>

          {/* Three platform cards */}
          <Box
            display="flex"
            flexDirection={{ xs: "column", md: "row" }}
            justifyContent="center"
            alignItems={{ xs: "center", md: "stretch" }}
            gap={3}
          >
            {/* Windows */}
            <Paper
              elevation={4}
              sx={{
                flex: "1 1 0",
                maxWidth: 280,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                p: 4,
                borderRadius: 4,
                bgcolor: "background.paper",
              }}
            >
              <LaptopWindows
                sx={{ fontSize: 52, mb: 1.5, color: "primary.main" }}
              />
              <Typography variant="h6" fontWeight={700} mb={4}>
                Windows
              </Typography>
              <Box sx={{ mt: "auto" }}>
                <Button
                  variant="contained"
                  color="secondary"
                  href={microsoftStoreUrl}
                  startIcon={<Download />}
                  sx={{
                    borderRadius: 999,
                    px: 3,
                    py: 1.5,
                    textTransform: "none",
                    fontWeight: "bold",
                    fontSize: "0.95rem",
                  }}
                >
                  {download_page.download_button[lang]}
                </Button>
              </Box>
            </Paper>

            {/* Google Play */}
            <Paper
              elevation={4}
              component="a"
              href={googlePlayUrl}
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                flex: "1 1 0",
                maxWidth: 280,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                p: 4,
                borderRadius: 4,
                bgcolor: "background.paper",
                textDecoration: "none",
                transition: "box-shadow .2s",
                "&:hover": { boxShadow: 8 },
              }}
            >
              <Android sx={{ fontSize: 52, mb: 1.5, color: "#3DDC84" }} />
              <Typography variant="h6" fontWeight={700} mb={3}>
                Google Play
              </Typography>
              {isMobile ? (
                <Button
                  variant="contained"
                  color="success"
                  href={googlePlayUrl}
                  startIcon={<Download />}
                  sx={{
                    borderRadius: 999,
                    px: 3,
                    py: 1.5,
                    textTransform: "none",
                    fontWeight: "bold",
                    fontSize: "0.95rem",
                  }}
                >
                  {download_page.google_play_button[lang]}
                </Button>
              ) : (
                <>
                  <QRCodeSVG value={googlePlayUrl} size={150} />
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    align="center"
                    sx={{ mt: 2 }}
                  >
                    {download_page.scan_to_download[lang]}
                  </Typography>
                </>
              )}
            </Paper>

            {/* App Store */}
            <Paper
              elevation={4}
              component="a"
              href={appStoreUrl}
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                flex: "1 1 0",
                maxWidth: 280,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                p: 4,
                borderRadius: 4,
                bgcolor: "background.paper",
                textDecoration: "none",
                transition: "box-shadow .2s",
                "&:hover": { boxShadow: 8 },
              }}
            >
              <Apple sx={{ fontSize: 52, mb: 1.5 }} />
              <Typography variant="h6" fontWeight={700} mb={3}>
                App Store
              </Typography>
              {isMobile ? (
                <Button
                  variant="contained"
                  color="primary"
                  href={appStoreUrl}
                  startIcon={<Download />}
                  sx={{
                    borderRadius: 999,
                    px: 3,
                    py: 1.5,
                    textTransform: "none",
                    fontWeight: "bold",
                    fontSize: "0.95rem",
                  }}
                >
                  {download_page.app_store_button[lang]}
                </Button>
              ) : (
                <>
                  <QRCodeSVG value={appStoreUrl} size={150} />
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    align="center"
                    sx={{ mt: 2 }}
                  >
                    {download_page.scan_to_download[lang]}
                  </Typography>
                </>
              )}
            </Paper>
          </Box>
        </Container>
      </FullBleed>
    </Box>
  );
}

export default DownloadPage;
